import type { FC } from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArticleFormat,
  BrandKey,
  QualifierOrderingInput,
  QualifierStatus,
} from '../../../../../../../../__generated__/queries-web';
import { useQualifiersGetter } from '../../../../../../../ArticleEdit/DrawerContent/getQualifers.web.graphql';
import type {
  CategoryFormSectionBoardQualifier,
  SectionForm,
} from '../../types';
import { useEditSection } from '../../useEditSection';
import { TabMultiselect } from '../TabMultiselect';
import {
  BOARD_TAB_QUALIFIERS_LABEL,
  BOARD_TAB_QUALIFIERS_MAX_QUANTITY,
  BOARD_TAB_QUALIFIERS_SEARCH_PLACEHOLDER,
} from '../TabMultiselect/constants';
import type { BoardProps } from '../';

type BoardQualifiersProps = Pick<BoardProps, 'id' | 'qualifiers'>;

export const BoardQualifiers: FC<BoardQualifiersProps> = ({
  id,
  qualifiers,
}) => {
  const { brandKey } = useParams<{ brandKey: BrandKey }>();
  const { handlers } = useEditSection(id);
  const getQualifiers = useQualifiersGetter();

  const handleQualifiersChange = useCallback(
    (newQualifiers: CategoryFormSectionBoardQualifier[]) => {
      handlers.handleSectionChange({
        fieldName: 'qualifiers' as keyof SectionForm,
        value: newQualifiers,
      });
    },
    [handlers],
  );

  const fetchList = useCallback(
    (search: string) =>
      getQualifiers({
        first: 100,
        where: {
          ...(search && { title_contains: search }),
          brandKey,
          format: ArticleFormat.Rich,
          status: QualifierStatus.Published,
        },
        orderBy: [QualifierOrderingInput.title_ASC],
      }).then((data) =>
        data?.qualifiers
          .filter(({ title }) => !!title)
          .map(({ id: qualifierId, title }) => ({
            id: qualifierId,
            label: title,
          })),
      ),
    [brandKey, getQualifiers],
  );

  return (
    <TabMultiselect
      chipVariant="qualifier"
      fetchList={fetchList}
      items={qualifiers}
      label={BOARD_TAB_QUALIFIERS_LABEL}
      maxQuantity={BOARD_TAB_QUALIFIERS_MAX_QUANTITY}
      onChange={handleQualifiersChange}
      searchPlaceholder={BOARD_TAB_QUALIFIERS_SEARCH_PLACEHOLDER}
    />
  );
};
